import { apiClient, unwrap } from '@/lib/api-client';
import type { ApiSuccess, PaginationMeta } from '@/types/api';
import type {
  SubscriptionChannel,
  SubscriptionConfirmation,
  SubscriptionConfirmationStatus,
} from '@/services/subscription.service';

/**
 * Sisi konsol platform dari pembayaran langganan: rekening tujuan, pemeriksaan
 * bukti, dan rekap pemasukan.
 *
 * Terpisah dari `subscription.service.ts` yang melayani lembaga. Bentuk datanya
 * dipakai bersama — `SubscriptionChannel` dan `SubscriptionConfirmation`
 * diimpor dari sana, bukan disalin — karena keduanya membaca baris yang sama
 * persis; menyalinnya berarti dua definisi yang boleh berbeda diam-diam.
 */

/** Satu kelompok pemasukan: per bulan, per paket, atau per lembaga. */
export interface RevenueBucket {
  label: string;
  count: number;
  /** Dasar Pengenaan Pajak — harga sebelum PPN. */
  base: number;
  vat: number;
  total: number;
}

export interface RevenueTotals extends Omit<RevenueBucket, 'label' | 'count'> {
  discount: number;
  invoiceCount: number;
  tenantCount: number;
}

export interface RevenueInvoice {
  id: string;
  number: string;
  tenantId: string;
  tenantName: string;
  planName: string;
  issuedAt: string;
  paidAt: string;
  base: number;
  vat: number;
  discount: number;
  total: number;
}

export interface RevenueReport {
  range: { from: string; to: string; timezone: string };
  vatLabel: string;
  vatRate: number;
  summary: RevenueTotals;
  monthly: RevenueBucket[];
  byPlan: RevenueBucket[];
  byTenant: RevenueBucket[];
  invoices: RevenueInvoice[];
}

export interface RevenueReportRange {
  from?: string;
  to?: string;
}

export interface SubscriptionConfirmationListResult {
  items: SubscriptionConfirmation[];
  meta: PaginationMeta;
}

export const subscriptionBillingService = {
  /** Termasuk rekening yang dimatikan — konsol mengelola daftar utuhnya. */
  listChannels(): Promise<SubscriptionChannel[]> {
    return unwrap(
      apiClient.get<ApiSuccess<SubscriptionChannel[]>>('/admin/subscription-billing/channels'),
    );
  },

  saveChannels(channels: SubscriptionChannel[]): Promise<SubscriptionChannel[]> {
    return unwrap(
      apiClient.put<ApiSuccess<SubscriptionChannel[]>>('/admin/subscription-billing/channels', {
        channels,
      }),
    );
  },

  async listConfirmations(
    params: { status?: SubscriptionConfirmationStatus; page?: number; perPage?: number } = {},
  ): Promise<SubscriptionConfirmationListResult> {
    const response = await apiClient.get<ApiSuccess<SubscriptionConfirmation[]>>(
      '/admin/subscription-billing/confirmations',
      { params },
    );

    return { items: response.data.data, meta: response.data.meta! };
  },

  async pendingCount(): Promise<number> {
    const result = await unwrap(
      apiClient.get<ApiSuccess<{ count: number }>>(
        '/admin/subscription-billing/confirmations/pending-count',
      ),
    );

    return result.count;
  },

  approve(id: string): Promise<SubscriptionConfirmation> {
    return unwrap(
      apiClient.post<ApiSuccess<SubscriptionConfirmation>>(
        `/admin/subscription-billing/confirmations/${id}/approve`,
      ),
    );
  },

  reject(id: string, reason: string): Promise<SubscriptionConfirmation> {
    return unwrap(
      apiClient.post<ApiSuccess<SubscriptionConfirmation>>(
        `/admin/subscription-billing/confirmations/${id}/reject`,
        { reason },
      ),
    );
  },

  report(params: RevenueReportRange = {}): Promise<RevenueReport> {
    return unwrap(
      apiClient.get<ApiSuccess<RevenueReport>>('/admin/subscription-billing/revenue', { params }),
    );
  },

  async exportReport(params: RevenueReportRange = {}): Promise<Blob> {
    const response = await apiClient.get('/admin/subscription-billing/revenue/export', {
      params,
      responseType: 'blob',
    });

    return response.data as Blob;
  },
};
